import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, ArrowRight, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateBasics() {
  const { user } = useAuth();
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      companyName: saved?.companyName || '',
      tagline: saved?.tagline || '',
      problem: saved?.problem || '',
      solution: saved?.solution || '',
      marketSize: saved?.marketSize || '',
      businessModel: saved?.businessModel || '',
      team: saved?.team || '',
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleNext = () => {
    navigate('/pitch/new/traction');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const fields = [
    { key: 'companyName', label: 'Company Name', type: 'input', placeholder: 'Enter your company name', hint: 'Make it memorable and relevant to your mission' },
    { key: 'tagline', label: 'Tagline', type: 'input', placeholder: 'One-line description of your venture', hint: 'Clear, concise, and compelling' },
    { key: 'problem', label: 'Problem', type: 'textarea', placeholder: 'Describe the problem you are solving in detail...', hint: 'Who has this problem? How painful is it? What\'s the current solution?' },
    { key: 'solution', label: 'Solution', type: 'textarea', placeholder: 'Describe your proposed solution in detail...', hint: 'How does it solve the problem? What makes it unique and defensible?' },
    { key: 'marketSize', label: 'Market Size', type: 'textarea', placeholder: 'Total Addressable Market (TAM), Serviceable Available Market (SAM)...', hint: 'Include numbers and sources. Be specific about market growth.' },
    { key: 'businessModel', label: 'Business Model', type: 'textarea', placeholder: 'How will you make money?', hint: 'Subscription? Marketplace? Enterprise SaaS? Unit economics?' },
    { key: 'team', label: 'Team', type: 'textarea', placeholder: 'Key team members, their backgrounds, and relevant experience...', hint: 'Include LinkedIn profiles, past exits, domain expertise' },
  ];

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Basics</h1>
            <p className="text-muted-foreground mt-1">Step 1 of 8 · Core information about your venture</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Company Overview</CardTitle>
            <CardDescription>
              Tell us about your company and the problem you're solving.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-blue-50 border-blue-200">
              <InfoIcon className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                Be specific and detailed. Our AI analyzes every word to provide accurate feedback.
              </AlertDescription>
            </Alert>

            {fields.map(f => (
              <div key={f.key} className="space-y-3">
                <Label htmlFor={f.key} className="text-lg font-medium">
                  {f.label}
                </Label>
                {f.type === 'input' ? (
                  <Input
                    id={f.key}
                    value={form[f.key as keyof typeof form]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="text-base py-6 px-4"
                  />
                ) : (
                  <div className="relative">
                    <Textarea
                      id={f.key}
                      value={form[f.key as keyof typeof form]}
                      onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="min-h-[300px] text-base p-4 resize-y"
                    />
                    <div className="absolute bottom-3 right-3 text-sm text-muted-foreground bg-background/90 px-3 py-1 rounded-full border shadow-sm">
                      {form[f.key as keyof typeof form].length} characters · {Math.ceil(form[f.key as keyof typeof form].length / 500)} min read
                    </div>
                  </div>
                )}
                {f.hint && (
                  <p className="text-sm text-muted-foreground italic">
                    💡 {f.hint}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: Traction
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}